import { DndContext, DragOverlay } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Control, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { companyQuery } from "#modules/company/infra/company.query";
import { CompanySettings } from "#modules/company/infra/CompanySettings.dto";
import { DefaultProcessStepFormModal } from "#modules/company/view/DefaultProcessStepFormModal";
import {
  DefaultProcessStepForTable,
  DefaultProcessStepsTable,
} from "#modules/company/view/DefaultProcessStepsTable";
import { useDefaultProcessStepsDrag } from "#modules/company/view/useDefaultProcessStepsDrag";
import { useUpdateDefaultProcessStep } from "#modules/defaultProcessSteps/view/useUpdateDefaultProcessStep";
import {
  computeNextDefaultProcessStepIndex,
  splitDefaultProcessSteps,
  withDefaultProcessStepSectionIndexes,
} from "#modules/onboardingV2/domain/splitDefaultProcessSteps";
import { Icon } from "#shared/icons/Icon";
import { Group } from "#shared/view/components/Group";
import { Paper } from "#shared/view/components/Paper";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Title } from "#shared/view/components/Title";
import { Button } from "#shared/view/designSystem/Button";

type DefaultProcessStepsSettingsProps = {
  control: Control<CompanySettings>;
  companyId: string;
};

export const DefaultProcessStepsSettings = ({
  control,
  companyId,
}: DefaultProcessStepsSettingsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { fields, remove, replace, append, update } = useFieldArray({
    control,
    name: "defaultProcessSteps",
    keyName: "__rhfId",
  });

  const invalidateCompanyQuery = () => {
    void queryClient.invalidateQueries(companyQuery());
  };

  const handleProcessStepUpdated = useCallback(
    (updated: DefaultProcessStepForTable) => {
      const fieldIndex = fields.findIndex((f) => f.id === updated.id);
      if (fieldIndex !== -1) {
        update(fieldIndex, updated);
      }
      void queryClient.invalidateQueries(companyQuery());
    },
    [fields, update, queryClient],
  );

  const { updateDefaultProcessStep } = useUpdateDefaultProcessStep({
    onSuccess: invalidateCompanyQuery,
  });

  const { beginning: beginningProcessSteps, end: endProcessSteps } = useMemo(
    () => splitDefaultProcessSteps(fields),
    [fields],
  );

  const getFieldIndex = useCallback(
    (processStepId: string | undefined) => {
      return fields.findIndex((f) => f.id === processStepId);
    },
    [fields],
  );

  const commitSections = useCallback(
    (nextSections: { beginning: typeof beginningProcessSteps; end: typeof endProcessSteps }) => {
      const reindexed = withDefaultProcessStepSectionIndexes(nextSections);
      const previousIndexById = new Map(fields.map((field) => [field.id, field.index]));
      const nextFields = [...reindexed.beginning, ...reindexed.end] as (typeof fields)[number][];
      const changedFields = nextFields.filter(
        (processStep) => previousIndexById.get(processStep.id) !== processStep.index,
      );

      if (changedFields.length === 0) {
        return;
      }

      replace(nextFields);
      changedFields.forEach((processStep) => {
        updateDefaultProcessStep({
          id: processStep.id,
          payload: { index: processStep.index },
        });
      });
    },
    [fields, replace, updateDefaultProcessStep],
  );

  const {
    sections,
    activeStep,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = useDefaultProcessStepsDrag({
    beginning: beginningProcessSteps,
    end: endProcessSteps,
    onCommit: commitSections,
  });

  const [beginningCreateOpened, { open: openBeginningCreate, close: closeBeginningCreate }] =
    useDisclosure(false);
  const [endCreateOpened, { open: openEndCreate, close: closeEndCreate }] = useDisclosure(false);

  return (
    <Stack gap="sm" maw="80%">
      <Title order={5}>{t("settings.defaultProcessSteps")}</Title>

      <Text size="sm" c="dimmed">
        {t("settings.defaultProcessStepsDescription")}
      </Text>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Stack gap="sm">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Title order={6}>{t("settings.beginningProcessSteps")}</Title>
                <Text size="xs" c="dimmed">
                  {t("settings.beginningProcessStepsDescription")}
                </Text>
              </Stack>
              <Button
                variant="outline"
                leftSection={<Icon name="plus" />}
                onClick={openBeginningCreate}
              >
                {t("settings.addBeginningProcessStep")}
              </Button>
            </Group>

            <DefaultProcessStepsTable
              companyId={companyId}
              section="beginning"
              processSteps={sections.beginning}
              onProcessStepUpdated={handleProcessStepUpdated}
              onProcessStepDeleted={(index) => {
                const fieldIndex = getFieldIndex(sections.beginning[index]?.id);
                if (fieldIndex !== -1) {
                  remove(fieldIndex);
                  invalidateCompanyQuery();
                }
              }}
            />
          </Stack>

          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Title order={6}>{t("settings.endProcessSteps")}</Title>
                <Text size="xs" c="dimmed">
                  {t("settings.endProcessStepsDescription")}
                </Text>
              </Stack>
              <Button variant="outline" leftSection={<Icon name="plus" />} onClick={openEndCreate}>
                {t("settings.addEndProcessStep")}
              </Button>
            </Group>

            <DefaultProcessStepsTable
              companyId={companyId}
              section="end"
              processSteps={sections.end}
              onProcessStepUpdated={handleProcessStepUpdated}
              onProcessStepDeleted={(index) => {
                const fieldIndex = getFieldIndex(sections.end[index]?.id);
                if (fieldIndex !== -1) {
                  remove(fieldIndex);
                  invalidateCompanyQuery();
                }
              }}
            />
          </Stack>
        </Stack>

        <DragOverlay>
          {activeStep ? (
            <Paper shadow="md" px="sm" py={4} radius="sm" bg="white">
              <Text size="xs">{t(`industrialProcesses.${activeStep.industrialProcess}`)}</Text>
            </Paper>
          ) : null}
        </DragOverlay>
      </DndContext>

      <DefaultProcessStepFormModal
        companyId={companyId}
        opened={beginningCreateOpened}
        onClose={closeBeginningCreate}
        mode="create"
        index={computeNextDefaultProcessStepIndex(beginningProcessSteps, "beginning")}
        onSuccess={(created) => {
          append(created);
          closeBeginningCreate();
          invalidateCompanyQuery();
        }}
      />

      <DefaultProcessStepFormModal
        companyId={companyId}
        opened={endCreateOpened}
        onClose={closeEndCreate}
        mode="create"
        index={computeNextDefaultProcessStepIndex(endProcessSteps, "end")}
        onSuccess={(created) => {
          append(created);
          closeEndCreate();
          invalidateCompanyQuery();
        }}
      />
    </Stack>
  );
};
